import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, fetchUserAttributes, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { deleteUserAccount } from '../utils/api';

interface User {
  userId: string;
  email: string;
  emailVerified: boolean;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOutUser: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadUser = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      setUser({
        userId: currentUser.userId,
        email: userAttributes.email || '',
        emailVerified: userAttributes.email_verified === 'true',
      });
    } catch (error) {
      // User not authenticated - this is expected when not logged in
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signOutUser = async (): Promise<void> => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const deleteAccount = async (): Promise<void> => {
    try {
      // Call backend Lambda to delete all user data and Cognito account
      const result = await deleteUserAccount();
      console.log('Account deletion results:', result);
      
      // This must happen after deletion so we can authenticate the delete request
      await signOut({ global: true });
      
      // Clear local user state
      setUser(null);
      
      // Clear any remaining auth data from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider') || 
            key.startsWith('amplify') || 
            key.startsWith('aws-amplify')) {
          localStorage.removeItem(key);
        }
      });
      
      console.log('Account deleted and all auth data cleared');
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error; // Re-throw to allow UI to show error message
    }
  };

  const refreshUser = async (): Promise<void> => {
    await loadUser();
  };

  useEffect(() => {
    loadUser();

    // Listen for auth events
    const authListener = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          loadUser();
          break;
        case 'signedOut':
          setUser(null);
          setIsLoading(false);
          break;
        case 'tokenRefresh':
          loadUser();
          break;
        default:
          // Ignore other auth events
          break;
      }
    });

    // Cleanup listener on unmount
    return () => {
      authListener();
    };
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signOutUser,
    deleteAccount,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 